import { useState } from 'react'
import { toast } from 'sonner'
import { Modal } from './Modal.js'
import { AdminApiError } from '../adminApi.js'
import {
  useArchiveBranch,
  useBranches,
  useCreateBranch,
  usePatchBranch,
  useUnarchiveBranch,
} from '../hooks/useBranches.js'
import { fmtDate } from '../format.js'
import type { BranchRow } from '../types.js'

/**
 * Branches tab under tenant detail. List with an "Include archived" toggle,
 * create form, inline rename, and archive / unarchive. Archived branches keep
 * their history but are hidden by default and block new stamps server-side.
 */
export function TenantBranches({ tenantId }: { tenantId: string }) {
  const [includeArchived, setIncludeArchived] = useState(false)
  const branches = useBranches(tenantId, includeArchived)
  const createBranch = useCreateBranch(tenantId)
  const patchBranch = usePatchBranch(tenantId)
  const archiveBranch = useArchiveBranch(tenantId)
  const unarchiveBranch = useUnarchiveBranch(tenantId)

  const [newName, setNewName] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  // Slice 4b — text fields for "paste coords from Google Maps." Admin UI
  // intentionally text-based; owner UI gets the map picker. Empty string =
  // "clear coord" intent; both must be empty or both must be parseable.
  const [editLat, setEditLat] = useState('')
  const [editLng, setEditLng] = useState('')
  // Branch pending archive (drives the reason modal).
  const [archiving, setArchiving] = useState<BranchRow | null>(null)
  const [archiveReason, setArchiveReason] = useState('')

  function handleCreate() {
    const name = newName.trim()
    if (!name) return
    createBranch.mutate(
      { name },
      {
        onSuccess: () => {
          toast.success('Branch created.')
          setNewName('')
        },
        onError: (err) =>
          toast.error(
            err instanceof AdminApiError
              ? err.userMessage
              : 'Could not create branch.',
          ),
      },
    )
  }

  function startEdit(branch: BranchRow) {
    setEditingId(branch.id)
    setEditName(branch.name)
    setEditLat(branch.latitude ?? '')
    setEditLng(branch.longitude ?? '')
  }

  function saveEdit() {
    if (!editingId) return
    const name = editName.trim()
    if (!name) return
    const latStr = editLat.trim()
    const lngStr = editLng.trim()
    // Both-or-neither. If only one is filled, surface the validation locally
    // before round-tripping the API (which would reject it with the same
    // message).
    if ((latStr === '') !== (lngStr === '')) {
      toast.error('Provide both latitude and longitude, or leave both empty.')
      return
    }
    let latitude: number | null | undefined = undefined
    let longitude: number | null | undefined = undefined
    if (latStr === '' && lngStr === '') {
      // Owner wants to clear an existing pin; send nulls.
      latitude = null
      longitude = null
    } else {
      const latNum = Number(latStr)
      const lngNum = Number(lngStr)
      if (!Number.isFinite(latNum) || latNum < -90 || latNum > 90) {
        toast.error('Latitude must be a number between -90 and 90.')
        return
      }
      if (!Number.isFinite(lngNum) || lngNum < -180 || lngNum > 180) {
        toast.error('Longitude must be a number between -180 and 180.')
        return
      }
      latitude = latNum
      longitude = lngNum
    }
    patchBranch.mutate(
      {
        branchId: editingId,
        name,
        latitude,
        longitude,
      },
      {
        onSuccess: () => {
          toast.success('Branch saved.')
          setEditingId(null)
        },
        onError: (err) =>
          toast.error(
            err instanceof AdminApiError
              ? err.userMessage
              : 'Could not save branch.',
          ),
      },
    )
  }

  function confirmArchive() {
    if (!archiving) return
    archiveBranch.mutate(
      {
        branchId: archiving.id,
        ...(archiveReason.trim() ? { reason: archiveReason.trim() } : {}),
      },
      {
        onSuccess: () => {
          toast.success(`${archiving.name} archived.`)
          setArchiving(null)
          setArchiveReason('')
        },
        onError: (err) =>
          toast.error(
            err instanceof AdminApiError
              ? err.userMessage
              : 'Could not archive branch.',
          ),
      },
    )
  }

  function handleUnarchive(branch: BranchRow) {
    unarchiveBranch.mutate(branch.id, {
      onSuccess: () => toast.success(`${branch.name} unarchived.`),
      onError: (err) =>
        toast.error(
          err instanceof AdminApiError
            ? err.userMessage
            : 'Could not unarchive branch.',
        ),
    })
  }

  const rows = branches.data?.branches ?? []

  return (
    <div>
      <div
        className="admin-page-head"
        style={{ marginBottom: 14 }}
      >
        <label className="admin-toggle">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
          />
          Include archived
        </label>
      </div>

      {/* Create form */}
      <div className="admin-card" style={{ marginBottom: 14 }}>
        <div className="admin-copy-row" style={{ marginBottom: 0 }}>
          <input
            className="admin-input"
            placeholder="New branch name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate()
            }}
          />
          <button
            type="button"
            className="admin-btn admin-btn-primary admin-btn-sm"
            onClick={handleCreate}
            disabled={createBranch.isPending || newName.trim() === ''}
          >
            Add branch
          </button>
        </div>
      </div>

      {branches.isLoading ? (
        <p className="admin-audit-meta">Loading branches…</p>
      ) : branches.isError ? (
        <p className="admin-audit-meta">Couldn't load branches.</p>
      ) : rows.length === 0 ? (
        <div className="admin-card">
          <div className="admin-empty">No branches yet.</div>
        </div>
      ) : (
        <div className="admin-card" style={{ padding: 0 }}>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Coords</th>
                  <th>Created</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((branch) => {
                  const isEditing = editingId === branch.id
                  const isArchived = branch.archived_at !== null
                  return (
                    <tr key={branch.id} style={{ cursor: 'default' }}>
                      <td data-label="Name">
                        {isEditing ? (
                          <input
                            className="admin-input"
                            value={editName}
                            onChange={(e) =>
                              setEditName(e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveEdit()
                              if (e.key === 'Escape')
                                setEditingId(null)
                            }}
                          />
                        ) : (
                          branch.name
                        )}
                      </td>
                      <td data-label="Coords">
                        {isEditing ? (
                          <div style={{ display: 'flex', gap: 6, maxWidth: '13rem' }}>
                            <input
                              className="admin-input"
                              placeholder="lat"
                              value={editLat}
                              onChange={(e) => setEditLat(e.target.value)}
                              style={{ width: '100%', minWidth: '4.5rem' }}
                            />
                            <input
                              className="admin-input"
                              placeholder="lng"
                              value={editLng}
                              onChange={(e) => setEditLng(e.target.value)}
                              style={{ width: '100%', minWidth: '4.5rem' }}
                            />
                          </div>
                        ) : branch.latitude && branch.longitude ? (
                          <span
                            className="admin-audit-meta"
                            style={{ fontFamily: 'ui-monospace, monospace' }}
                          >
                            {Number(branch.latitude).toFixed(5)},{' '}
                            {Number(branch.longitude).toFixed(5)}
                          </span>
                        ) : (
                          <span className="admin-audit-meta">—</span>
                        )}
                      </td>
                      <td className="admin-audit-meta" data-label="Created">
                        {fmtDate(branch.created_at)}
                      </td>
                      <td data-label="Status">
                        {isArchived ? (
                          <span className="admin-badge admin-badge-neutral">
                            Archived
                          </span>
                        ) : (
                          <span className="admin-badge admin-badge-ok">
                            Active
                          </span>
                        )}
                      </td>
                      <td data-label="Actions">
                        <div className="admin-row-actions">
                          {isEditing ? (
                            <>
                              <button
                                type="button"
                                className="admin-btn admin-btn-sm admin-btn-primary"
                                onClick={saveEdit}
                                disabled={patchBranch.isPending}
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="admin-btn admin-btn-sm"
                                onClick={() => setEditingId(null)}
                              >
                                Cancel
                              </button>
                            </>
                          ) : isArchived ? (
                            <button
                              type="button"
                              className="admin-btn admin-btn-sm"
                              onClick={() => handleUnarchive(branch)}
                              disabled={unarchiveBranch.isPending}
                            >
                              Unarchive
                            </button>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="admin-btn admin-btn-sm"
                                onClick={() => startEdit(branch)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="admin-btn admin-btn-sm admin-btn-danger"
                                onClick={() => {
                                  setArchiving(branch)
                                  setArchiveReason('')
                                }}
                              >
                                Archive
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {archiving && (
        <Modal
          title={`Archive ${archiving.name}`}
          onClose={() => setArchiving(null)}
        >
          <p className="admin-audit-meta" style={{ marginTop: 0 }}>
            Archived branches keep their stamp history but cannot accept new
            stamp operations. You can unarchive later.
          </p>
          <div className="admin-field">
            <label className="admin-label" htmlFor="archive-reason">
              Reason (optional)
            </label>
            <textarea
              id="archive-reason"
              className="admin-textarea"
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
            />
          </div>
          <div className="admin-form-actions">
            <button
              type="button"
              className="admin-btn admin-btn-danger"
              onClick={confirmArchive}
              disabled={archiveBranch.isPending}
            >
              {archiveBranch.isPending ? 'Archiving…' : 'Archive branch'}
            </button>
            <button
              type="button"
              className="admin-btn"
              onClick={() => setArchiving(null)}
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}
